// Copyright (c) Docugami, Inc. All rights reserved.

const { v4: uuidv4 } = require('uuid');

interface ActionItem extends IActionItem<any, any> {
}

export interface IActionItem<TReturn, TMetadata> {
    action: () => Promise<TReturn>;
    onStart: (item: IActionItem<TReturn, TMetadata>) => void;
    onComplete: (item: IActionItem<TReturn, TMetadata>, result?: TReturn, error?: any) => void;
    token: string;
    metadata: TMetadata;
}

export interface IActionStats {
    totalCount: number,
    remainingCount: number,
    completedCount: number,
    succeededCount: number,
    failedCount: number
}

export interface ActionQueueConfiguration {
    autoRun: boolean;
    concurrentActions: number;
}

export class AsyncActionQueue {
    private _queue: ActionItem[] = [];
    private _runningActions: Map<ActionItem, Promise<void>> = new Map();
    private _runner: Promise<void> | null = null;
    private readonly _configuration: ActionQueueConfiguration;
    private _stats: IActionStats = {
        totalCount: 0,
        remainingCount: 0,
        completedCount: 0,
        succeededCount: 0,
        failedCount: 0
    };

    get stats(): IActionStats { return this._stats; }
    get isRunning(): boolean { return !!this._runner; }

    constructor(configuration: ActionQueueConfiguration) {
        this._configuration = configuration;
    }

    enqueue<TReturn, TMetadata>(action: () => Promise<TReturn>, metadata?: TMetadata, onStart?: (item: IActionItem<TReturn, TMetadata>) => void, onComplete?: (item: IActionItem<TReturn, TMetadata>, result?: TReturn, error?: any) => void): { promise: Promise<TReturn>, token: string } {
        const token = uuidv4();
        const promise = new Promise<TReturn>((resolve, reject) => {
            const completeCallback = (item: ActionItem, result?: any, error?: any) => {
                // If a completion callback was provided, then we expect the consumer to handle the results there as opposed
                // to the promise
                if (!!onComplete) {
                    onComplete(item as IActionItem<TReturn, TMetadata>, result as TReturn, error);
                    resolve(result);
                }
                else if (!!error) {
                    reject(error);
                }
                else {
                    resolve(result)
                }
            }

            const startCallback = (item: ActionItem) => {
                if (!!onStart) {
                    onStart(item as IActionItem<TReturn, TMetadata>);
                }
            }

            this._queue.push({
                token: token,
                action: action,
                onStart: startCallback,
                onComplete: completeCallback,
                metadata: metadata
            });
        });

        if (this._configuration.autoRun) {
            this.run();
        }

        this._stats.totalCount++;
        this._stats.remainingCount = this._queue.length;

        return { promise, token };
    }

    cancel(token: string): boolean {
        const index = this._queue.findIndex(x => x.token === token);
        if (index !== -1) {
            this._queue.splice(index, 1);

            this._stats.totalCount--;
            this._stats.remainingCount = this._queue.length;

            return true;
        }

        return false;
    }

    run(): Promise<void> {
        if (!this._runner) {
            this._runner = this.runInternal();
        }

        return this._runner;
    }

    getPendingActions<TResult, TMetadata>(): IActionItem<TResult, TMetadata>[] {
        return this._queue;
    }

    getRunningActions<TResult, TMetadata>(): IActionItem<TResult, TMetadata>[] {
        return Array.from(this._runningActions.keys());
    }

    async reset(): Promise<void> {
        this._queue = [];
        if (!!this._runner) {
            await this._runner;
        }

        this._runningActions.clear();
        this._stats = {
            totalCount: 0,
            remainingCount: 0,
            completedCount: 0,
            succeededCount: 0,
            failedCount: 0
        };
    }

    private async runInternal(): Promise<void> {
        while (this._queue.length > 0 || this._runningActions.size > 0) {
            if (this._queue.length === 0) {
                // No items left, just wait for everything to complete
                await Promise.allSettled(Array.from(this._runningActions.values()));
                this._runningActions.clear();
            }
            else if (this._runningActions.size < this._configuration.concurrentActions) {
                // We have room for another action - start it
                const item = this._queue.shift()!;
                this._runningActions.set(item, this.runItem(item));
            }
            else {
                // There are more items, but our concurrent actions are full so let's wait for one to finish
                await Promise.race(Array.from(this._runningActions.values()));
            }
        }

        this._runningActions.clear();
        this._runner = null;
    }

    private async runItem(item: ActionItem): Promise<void> {
        let result: any;
        let actionError: any;

        try {
            item.onStart(item);
            result = await item.action();
            this._stats.succeededCount++;
        }
        catch (error) {
            actionError = error;
            this._stats.failedCount++;
        }

        this._stats.completedCount++;
        this._stats.remainingCount = this._queue.length;
        this._runningActions.delete(item);

        item.onComplete(item, result, actionError);
    }
}
